import { useEffect, useState } from 'react'
import { AlertTriangle, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { hasAuthPresence } from '@/lib/authPresence'

// Shown when a signed-in user lands in the demo (e.g. an old bookmark or a stray
// link to the public host). The demo can't read studio's session directly, so we
// rely on the shared-domain presence cookie. Warns that demo work is not saved or
// carried over and points them to the studio, where their work persists. Not
// dismissible on purpose – the "work won't be saved" warning must stay visible.
export default function DemoLoggedInBanner({ studioHref, surfaceLabel = 'the demo' }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const sync = () => setVisible(hasAuthPresence())

    sync()
    // The user may sign in on another tab, or the cookie may land just after
    // mount – re-check when the demo tab regains focus.
    window.addEventListener('focus', sync)
    document.addEventListener('visibilitychange', sync)
    return () => {
      window.removeEventListener('focus', sync)
      document.removeEventListener('visibilitychange', sync)
    }
  }, [])

  if (!visible) return null

  return (
    <div className="shrink-0 border-b border-amber-300/70 bg-amber-50 text-amber-950">
      <div className="mx-auto flex w-full max-w-7xl items-center gap-3 px-3 py-2 sm:px-4">
        <AlertTriangle className="hidden h-4 w-4 shrink-0 text-amber-600 sm:block" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-xs leading-snug sm:text-[13px]">
          <span className="font-semibold">You&rsquo;re signed in.</span>{' '}
          This is {surfaceLabel} – anything you do here won&rsquo;t be saved or carried over to your account.
          Open the studio to keep your work.
        </p>
        <Button asChild size="sm" className="shrink-0 dark:border-black/25">
          <a href={studioHref}>
            Go
            <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </Button>
      </div>
    </div>
  )
}
